import type { Owner } from "../types";
import { pendingChanged } from "./pending-entries";
import { resolveDeferredPayee, shouldDeferCreate } from "./offline-associated";

export const RECORD_PREFIX = "barnyard-pending-record-";

/** Loan create payload as the IOU form posts it, plus an optional deferred payee name. */
export type PendingLoanDraft = {
  payeeId: string;
  payeeName?: string;
  direction: "Owed to us" | "We owe";
  originalAmount: string;
  description: string;
  createdDate: string;
  dueDate: string;
  fundingAccountId: string;
};

/** Bill create/update payload as the Bills form posts it. */
export type PendingBillDraft = {
  id?: string;
  expectedUpdatedAt?: number;
  type: "Expense" | "Income" | "Transfer";
  name: string;
  accountId: string;
  destinationAccountId: string;
  payeeId: string;
  payeeName?: string;
  category: string;
  subcategory: string;
  amount: string;
  currency: string;
  frequency: "weekly" | "monthly" | "yearly";
  startDate: string;
  endDate: string;
  reminderDaysBefore: string;
  nextDueDate: string;
  sourceTransactionId?: string;
};

export type PendingLoanRecord = {
  kind: "loan";
  id: string;
  owner: Owner;
  draft: PendingLoanDraft;
};

export type PendingSettleRecord = {
  kind: "settle";
  id: string;
  owner: Owner;
  body: { iouId: string; amount: string; date: string; accountId: string; notes: string };
};

export type PendingBillRecord = {
  kind: "bill";
  id: string;
  owner: Owner;
  draft: PendingBillDraft;
  /** create posts to bills/create; update posts to bills/update. */
  action: "create" | "update";
};

export type PendingRecord = PendingLoanRecord | PendingSettleRecord | PendingBillRecord;

export function readPendingRecords(): { key: string; record: PendingRecord }[] {
  const items: { key: string; record: PendingRecord }[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(RECORD_PREFIX)) continue;
    try {
      const record = JSON.parse(localStorage.getItem(key) || "null") as PendingRecord | null;
      if (record?.id && (record.kind === "loan" || record.kind === "settle" || record.kind === "bill"))
        items.push({ key, record });
    } catch { /* Ignore malformed records. */ }
  }
  return items;
}

export function enqueuePendingRecord(record: PendingRecord) {
  const key = RECORD_PREFIX + record.id;
  if (!localStorage.getItem(key)) localStorage.setItem(key, JSON.stringify(record));
  pendingChanged();
  return key;
}

export function removePendingRecord(id: string) {
  localStorage.removeItem(RECORD_PREFIX + id);
  pendingChanged();
}

async function postJson(request: typeof fetch, path: string, body: unknown) {
  const response = await request(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(45000),
  });
  const value = await response.json() as { id?: string; error?: string };
  if (!response.ok) throw new Error(value.error || "Could not sync a saved record.");
  return value;
}

async function syncRecord(key: string, record: PendingRecord, request: typeof fetch) {
  if (record.kind === "loan") {
    const payeeId = await resolveDeferredPayee(record.draft.payeeId, record.draft.payeeName, request);
    if (!payeeId) throw new Error("Choose or add a payee before syncing this IOU.");
    await postJson(request, "/api/ious/create", {
      ...record.draft,
      payeeId,
      requestId: record.id,
    });
  } else if (record.kind === "settle") {
    if (record.body.iouId.startsWith("local:"))
      throw new Error("Wait for the IOU to sync before recording a repayment.");
    await postJson(request, "/api/ious/settle", { ...record.body, requestId: record.id });
  } else {
    const payeeId = await resolveDeferredPayee(record.draft.payeeId, record.draft.payeeName, request);
    const path = record.action === "update" ? "/api/bills/update" : "/api/bills/create";
    await postJson(request, path, {
      ...record.draft,
      payeeId: payeeId || undefined,
      destinationAccountId: record.draft.type === "Transfer" ? record.draft.destinationAccountId : undefined,
      requestId: record.id,
    });
  }
  localStorage.removeItem(key);
  pendingChanged();
}

/** Finish queued loan and bill writes after payees are resolved. */
export async function syncPendingRecords(request: typeof fetch = fetch): Promise<{ confirmed: number; errors: string[] }> {
  let confirmed = 0;
  const errors: string[] = [];
  for (const { key, record } of readPendingRecords()) {
    if (typeof navigator !== "undefined" && !navigator.onLine) break;
    try {
      await syncRecord(key, record, request);
      confirmed++;
    } catch (error) {
      errors.push(error instanceof Error ? error.message : "A loan or bill is still waiting to sync.");
    }
  }
  return { confirmed, errors };
}

/** Save online, or keep a durable local record when the network cannot confirm. */
export async function saveRecordOrQueue<T>(
  onlineSave: () => Promise<T>,
  queue: () => void,
): Promise<{ queued: boolean; result?: T }> {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    queue();
    return { queued: true };
  }
  try {
    return { queued: false, result: await onlineSave() };
  } catch (error) {
    if (!shouldDeferCreate(error)) throw error;
    queue();
    return { queued: true };
  }
}

function hasDeferredPayee(payeeId: string | undefined, payeeName: string | undefined) {
  return !!payeeName || !!payeeId?.startsWith("local:");
}

/** Resolve a deferred payee when online, otherwise queue the loan create. */
export async function saveLoanOrQueue(
  owner: Owner,
  draft: PendingLoanDraft,
  onlineSave: (draft: PendingLoanDraft) => Promise<unknown>,
): Promise<{ queued: boolean }> {
  const id = crypto.randomUUID();
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    enqueuePendingRecord({ kind: "loan", id, owner, draft });
    return { queued: true };
  }
  try {
    const payeeId = hasDeferredPayee(draft.payeeId, draft.payeeName)
      ? await resolveDeferredPayee(draft.payeeId, draft.payeeName)
      : draft.payeeId;
    await onlineSave({ ...draft, payeeId, payeeName: undefined });
    return { queued: false };
  } catch (error) {
    if (!shouldDeferCreate(error)) throw error;
    enqueuePendingRecord({ kind: "loan", id, owner, draft });
    return { queued: true };
  }
}

/** Resolve a deferred payee when online, otherwise queue the bill create/update. */
export async function saveBillOrQueue(
  owner: Owner,
  draft: PendingBillDraft,
  action: "create" | "update",
  onlineSave: (draft: PendingBillDraft) => Promise<unknown>,
): Promise<{ queued: boolean }> {
  const id = crypto.randomUUID();
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    enqueuePendingRecord({ kind: "bill", id, owner, draft, action });
    return { queued: true };
  }
  try {
    const payeeId = hasDeferredPayee(draft.payeeId, draft.payeeName)
      ? await resolveDeferredPayee(draft.payeeId, draft.payeeName)
      : draft.payeeId;
    await onlineSave({ ...draft, payeeId, payeeName: undefined });
    return { queued: false };
  } catch (error) {
    if (!shouldDeferCreate(error)) throw error;
    enqueuePendingRecord({ kind: "bill", id, owner, draft, action });
    return { queued: true };
  }
}
